import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/navigation";
import { FiArrowLeft } from "react-icons/fi";
import { FieldValues, UseFormReturn } from "react-hook-form";

import { Typography } from "@/components/Typography";
import { Button as ButtonLib } from "@npm_leadtech/qr-ui-components";
import { Form } from "@/components/Form";

import postRecoverPasswordPostHandler from "@/interface/api/postRecoverPassword";
import { PostRecoverPasswordResponse } from "@/interface/api/postRecoverPassword/types";

import { forgotPasswordValidation } from "@/utils/validators/forgot-password";
import { onBindClick } from "@/utils/formHelper";
import { paths } from "@/utils/paths";

import { useApiWithLayout } from "@/hooks/api";

import { ForgotPasswordProps } from "./types";
import { PAGES } from "@/internals/constants/pages";

export default function ForgotPassword({
  onSetFormType,
  forgotPasswordData,
  onForgotPassword,
}: ForgotPasswordProps) {
  const { t } = useTranslation(PAGES.FORGOT_PASSWORD);
  const router = useRouter();
  const [successSubmit, setSuccessSubmit] = useState(null);

  const [formObject, setFormObject] = useState<
    { values: FieldValues; methods: UseFormReturn } | undefined
  >(undefined);

  const onForgotPasswordRequest = (values: FieldValues) => {
    request({
      email: values.email,
      password: values.password,
    });
  };

  const schema = forgotPasswordValidation(forgotPasswordData);
  const { request, loading, data, error } = useApiWithLayout<PostRecoverPasswordResponse>(
    postRecoverPasswordPostHandler.postRecoverPassword
  );

  onBindClick(
    forgotPasswordData.formFields,
    "forgotPasswordCancel",
    () => !loading && onSetFormType("login")
  );

  const onForgotPasswordSubmit = (values: FieldValues, methods: UseFormReturn) => {
    setFormObject({ values, methods });
    onForgotPasswordRequest(values);
    setSuccessSubmit(true);
  };

  const handleBackClick = () => router.push(paths.login());

  const handleSuccessSubmit = () => router.replace(paths.login());

  const handleResendEmail = () => setSuccessSubmit(null);

  useEffect(() => {
    if (formObject && (data?.status === 204 || data?.status === 200) && !loading) {
      setSuccessSubmit(true);
    }
  }, [data]);

  useEffect(() => {
    if (formObject && error) {
      if (error.status === 400) {
        onForgotPassword(formObject.values, formObject.methods, forgotPasswordData);
      }
    }
  }, [error]);

  return (
    <div
      className="recover-password"
      data-testid="recover-password"
    >
      {successSubmit === null && (
        <>
          <ButtonLib
            id="back-button"
            size="small"
            shape="ghost"
            variant="neutral"
            iconLeft={<FiArrowLeft />}
            label={forgotPasswordData.goBack}
            dataQA="back-button"
            onClick={handleBackClick}
          />

          <Typography
            Tag="p"
            classname="recover-password__title"
            size="xl"
            spacing={5}
          >
            {forgotPasswordData.title}
          </Typography>

          <Typography
            Tag="p"
            classname="recover-password__subtitle"
            spacing={5}
          >
            {forgotPasswordData.subtitle}
          </Typography>

          <Form
            fields={forgotPasswordData.formFields}
            onSubmit={onForgotPasswordSubmit}
            schema={schema}
            validationMode="onSubmit"
            isLoading={loading && ["email", "recoverPasswordReset"]}
            isAuth
          />
        </>
      )}

      {successSubmit && (
        <>
          <Typography
            Tag="p"
            classname="recover-password__title"
            size="xl"
            spacing={5}
          >
            {forgotPasswordData.successTitle}
          </Typography>

          <Typography
            Tag="p"
            classname="recover-password__subtitle"
            spacing={5}
          >
            {t("success_subtitle_1")} {formObject.values?.email} {t("success_subtitle_2")}
          </Typography>

          <ul className="recover-password__actions">
            <li className="recover-password__actions-item">
              <ButtonLib
                id="success-button"
                variant="secondary"
                label={forgotPasswordData.successButton}
                dataQA="success-button"
                onClick={handleSuccessSubmit}
              />
            </li>
            <li className="recover-password__actions-item">
              <ButtonLib
                id="resend-email-button"
                shape="ghost"
                label={forgotPasswordData.resendEmailLink}
                dataQA="resend-email-button"
                onClick={handleResendEmail}
              />
            </li>
          </ul>
        </>
      )}
    </div>
  );
}
